// C:\Users\purplehat\Desktop\payment-cookie-system\frontend\src\components\Profile.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  ArrowRightOnRectangleIcon,
  WalletIcon,
  ClockIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  BellIcon,
  BellAlertIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

// ============================================================
// Helpers para Web Push (inline para no crear otro archivo)
// ============================================================
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function Profile() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Estados del formulario de cambio de contraseña
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState(null);
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });

  // ✅ Estados de notificaciones push
  const [pushStatus, setPushStatus] = useState('unknown'); // unknown | unsupported | denied | subscribed | unsubscribed | ios-not-installed
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  // ✅ Detectar estado inicial de notificaciones
  useEffect(() => {
    let cancelled = false;

    const checkStatus = async () => {
      // Si es iOS y no está instalada, mostrar aviso especial
      if (isIOS() && !isStandalone()) {
        if (!cancelled) setPushStatus('ios-not-installed');
        return;
      }

      if (!pushSupported()) {
        if (!cancelled) setPushStatus('unsupported');
        return;
      }

      if (Notification.permission === 'denied') {
        if (!cancelled) setPushStatus('denied');
        return;
      }

      try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (!reg) {
          if (!cancelled) setPushStatus('unsubscribed');
          return;
        }
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setPushStatus(sub ? 'subscribed' : 'unsubscribed');
      } catch (e) {
        if (!cancelled) setPushStatus('unsubscribed');
      }
    };

    checkStatus();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError(null);

    if (pwdForm.next.length < 6) {
      setPwdError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setPwdError('Las contraseñas nuevas no coinciden');
      return;
    }
    if (pwdForm.current === pwdForm.next) {
      setPwdError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setPwdLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        currentPassword: pwdForm.current,
        newPassword: pwdForm.next
      });
      if (res.data.success) {
        toast.success('🔑 Contraseña actualizada correctamente');
        setPwdForm({ current: '', next: '', confirm: '' });
        setPwdError(null);
      } else {
        setPwdError(res.data.error || 'No se pudo cambiar la contraseña');
      }
    } catch (err) {
      setPwdError(err.response?.data?.error || 'Error al cambiar la contraseña');
    } finally {
      setPwdLoading(false);
    }
  };

  // ============================================================
  // ✅ Suscribirse a notificaciones push
  // ============================================================
  const handleEnableNotifications = async () => {
    // iOS sin instalar
    if (isIOS() && !isStandalone()) {
      toast.error('En iPhone primero añade esta web a tu pantalla de inicio (Compartir → Añadir a pantalla de inicio) y ábrela desde ahí.', {
        duration: 7000
      });
      return;
    }

    if (!pushSupported()) {
      toast.error('Tu navegador no soporta notificaciones push');
      return;
    }

    setPushLoading(true);
    try {
      // 1. Pedir permiso
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushStatus('denied');
        toast.error('Permiso de notificaciones denegado');
        return;
      }

      // 2. Registrar Service Worker
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // 3. Suscribir
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        toast.error('Falta configurar REACT_APP_VAPID_PUBLIC_KEY en el frontend');
        return;
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // 4. Enviar al backend
      await api.post('/push/subscribe', sub);

      setPushStatus('subscribed');
      toast.success('🔔 Notificaciones activadas');
    } catch (err) {
      console.error('Error suscribiendo a push:', err);
      toast.error(err.response?.data?.error || err.message || 'No se pudieron activar las notificaciones');
    } finally {
      setPushLoading(false);
    }
  };

  // ============================================================
  // ✅ Desuscribirse
  // ============================================================
  const handleDisableNotifications = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          // Avisar al backend que elimine la suscripción
          try {
            await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
          } catch (e) { /* ignorar */ }
          await sub.unsubscribe();
        }
      }
      setPushStatus('unsubscribed');
      toast.success('Notificaciones desactivadas');
    } catch (err) {
      console.error('Error desuscribiendo:', err);
      toast.error('No se pudieron desactivar las notificaciones');
    } finally {
      setPushLoading(false);
    }
  };

  // ✅ Prueba: enviar notificación desde el backend
  const handleTestNotification = async () => {
    try {
      await api.post('/push/test');
      toast.success('Notificación de prueba enviada');
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo enviar la prueba');
    }
  };

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loader" style={{ margin: '0 auto 1rem' }}></div>
          <p>Cargando perfil...</p>
        </div>
      </div>
    );
  }

  const isPending = user.status === 'pending' || user.role === 'pending';

  const pwdInputStyle = {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 0.75rem',
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: '0.5rem',
    color: '#ffffff',
    fontSize: '0.9rem',
    outline: 'none'
  };

  // Texto y estado del botón de notificaciones
  const renderNotificationSection = () => {
    if (pushStatus === 'ios-not-installed') {
      return (
        <div style={{
          background: '#00ffff10',
          border: '1px solid #00ffff40',
          borderRadius: '0.75rem',
          padding: '0.85rem',
          display: 'flex',
          gap: '0.6rem',
          alignItems: 'flex-start'
        }}>
          <DevicePhoneMobileIcon style={{ width: '1.3rem', color: '#00ffff', flexShrink: 0 }} />
          <div style={{ fontSize: '0.8rem', color: '#9ca3af', lineHeight: 1.5 }}>
            <strong style={{ color: '#fff' }}>iPhone detectado.</strong> Para recibir notificaciones,
            toca <strong style={{ color: '#00ffff' }}>Compartir</strong> en Safari →{' '}
            <strong style={{ color: '#00ffff' }}>Añadir a pantalla de inicio</strong>,
            y luego abre la app desde el icono.
          </div>
        </div>
      );
    }

    if (pushStatus === 'unsupported') {
      return (
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', padding: '0.5rem 0' }}>
          Tu navegador no soporta notificaciones push.
        </div>
      );
    }

    if (pushStatus === 'denied') {
      return (
        <div style={{ fontSize: '0.8rem', color: '#ff8095', padding: '0.5rem 0' }}>
          Permiso de notificaciones bloqueado. Actívalo desde la configuración del navegador.
        </div>
      );
    }

    if (pushStatus === 'subscribed') {
      return (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#00ff88',
            fontSize: '0.85rem',
            marginBottom: '0.75rem'
          }}>
            <CheckCircleIcon style={{ width: '1.1rem' }} />
            Notificaciones activadas
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleTestNotification}
              style={{
                flex: 1,
                padding: '0.6rem',
                background: 'none',
                border: '1px solid #00ffff',
                borderRadius: '0.5rem',
                color: '#00ffff',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
            >
              Enviar prueba
            </button>
            <button
              onClick={handleDisableNotifications}
              disabled={pushLoading}
              style={{
                flex: 1,
                padding: '0.6rem',
                background: 'none',
                border: '1px solid #ff0033',
                borderRadius: '0.5rem',
                color: '#ff0033',
                fontSize: '0.8rem',
                cursor: pushLoading ? 'not-allowed' : 'pointer',
                opacity: pushLoading ? 0.5 : 1
              }}
            >
              Desactivar
            </button>
          </div>
        </div>
      );
    }

    // unsubscribed / unknown
    return (
      <button
        onClick={handleEnableNotifications}
        disabled={pushLoading}
        style={{
          width: '100%',
          padding: '0.85rem',
          background: pushLoading ? '#333' : 'linear-gradient(135deg, #00ffff, #0088ff)',
          border: 'none',
          borderRadius: '0.5rem',
          color: pushLoading ? '#666' : '#000',
          fontWeight: 700,
          fontSize: '0.9rem',
          cursor: pushLoading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}
      >
        <BellAlertIcon style={{ width: '1.2rem' }} />
        {pushLoading ? 'Activando...' : 'Activar notificaciones'}
      </button>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#000000',
      color: '#ffffff',
      padding: '1rem',
      paddingBottom: '6rem'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem 0',
        marginBottom: '2rem'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#00ffff', cursor: 'pointer' }}
        >
          <ArrowLeftIcon style={{ width: '1.5rem', height: '1.5rem' }} />
        </button>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Mi Perfil
        </h1>
      </div>

      {/* Pendiente */}
      {isPending && (
        <div style={{
          background: '#ff660020',
          border: '1px solid #ff6600',
          borderRadius: '1rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <ClockIcon style={{ width: '1.5rem', height: '1.5rem', color: '#ff6600' }} />
          <div>
            <div style={{ fontWeight: 'bold', color: '#ff6600' }}>Cuenta pendiente de aprobación</div>
            <div style={{ fontSize: '0.8rem', color: '#ffaa66' }}>
              Tu cuenta está siendo revisada por un administrador.
              Recibirás acceso completo una vez sea aprobada.
            </div>
          </div>
        </div>
      )}

      {/* Avatar */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div style={{
          width: '6rem',
          height: '6rem',
          borderRadius: '3rem',
          background: isPending
            ? 'linear-gradient(135deg, #ff6600, #ffaa00)'
            : 'linear-gradient(135deg, #00ffff, #ff00ff)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1rem'
        }}>
          <span style={{ fontSize: '2.5rem' }}>{isPending ? '⏳' : '👤'}</span>
        </div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
          {user.name || user.username}
        </h2>
        <p style={{ color: '#9ca3af' }}>@{user.username}</p>
        {isPending && (
          <span style={{
            marginTop: '0.5rem',
            background: '#ff660020',
            color: '#ff6600',
            padding: '0.25rem 0.75rem',
            borderRadius: '1rem',
            fontSize: '0.7rem'
          }}>
            Pendiente de aprobación
          </span>
        )}
      </div>

      {/* Info cuenta */}
      <div style={{
        background: '#111111',
        border: '1px solid #222222',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1rem'
      }}>
        <h3 style={{ color: '#00ffff', marginBottom: '1rem' }}>Información de la cuenta</h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <EnvelopeIcon style={{ width: '1.25rem', height: '1.25rem', color: '#9ca3af' }} />
          <div>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Email</p>
            <p style={{ color: '#ffffff' }}>{user.email}</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <ShieldCheckIcon style={{ width: '1.25rem', height: '1.25rem', color: '#9ca3af' }} />
          <div>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Rol</p>
            <p style={{
              color: isPending ? '#ff6600' :
                user.role === 'owner' ? '#ff00ff' :
                  user.role === 'admin' ? '#00ffff' : '#ccff00',
              textTransform: 'capitalize'
            }}>
              {isPending ? 'Pendiente de aprobación' : user.role}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <CalendarIcon style={{ width: '1.25rem', height: '1.25rem', color: '#9ca3af' }} />
          <div>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Miembro desde</p>
            <p style={{ color: '#ffffff' }}>
              {new Date(user.createdAt || Date.now()).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* ✅ NOTIFICACIONES PUSH */}
      {!isPending && (
        <div style={{
          background: '#111111',
          border: '1px solid #222222',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '1rem'
        }}>
          <h3 style={{
            color: '#00ffff',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <BellIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            Notificaciones
          </h3>
          {renderNotificationSection()}
        </div>
      )}

      {/* ✅ Cambiar contraseña */}
      <div style={{
        background: '#111111',
        border: '1px solid #222222',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          color: '#00ffff',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <KeyIcon style={{ width: '1.25rem', height: '1.25rem' }} />
          Cambiar contraseña
        </h3>

        <form onSubmit={handleChangePassword}>
          {/* Actual */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>
              Contraseña actual
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPwd.current ? 'text' : 'password'}
                value={pwdForm.current}
                onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                required
                style={pwdInputStyle}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd({ ...showPwd, current: !showPwd.current })}
                style={{
                  position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex'
                }}
                tabIndex={-1}
              >
                {showPwd.current
                  ? <EyeSlashIcon style={{ width: '1.1rem' }} />
                  : <EyeIcon style={{ width: '1.1rem' }} />}
              </button>
            </div>
          </div>

          {/* Nueva */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>
              Nueva contraseña (mín. 6 caracteres)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPwd.next ? 'text' : 'password'}
                value={pwdForm.next}
                onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
                required
                minLength={6}
                style={pwdInputStyle}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd({ ...showPwd, next: !showPwd.next })}
                style={{
                  position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex'
                }}
                tabIndex={-1}
              >
                {showPwd.next
                  ? <EyeSlashIcon style={{ width: '1.1rem' }} />
                  : <EyeIcon style={{ width: '1.1rem' }} />}
              </button>
            </div>
          </div>

          {/* Confirmar */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>
              Confirmar nueva contraseña
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPwd.confirm ? 'text' : 'password'}
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                required
                minLength={6}
                style={pwdInputStyle}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd({ ...showPwd, confirm: !showPwd.confirm })}
                style={{
                  position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex'
                }}
                tabIndex={-1}
              >
                {showPwd.confirm
                  ? <EyeSlashIcon style={{ width: '1.1rem' }} />
                  : <EyeIcon style={{ width: '1.1rem' }} />}
              </button>
            </div>
          </div>

          {pwdError && (
            <div style={{
              background: '#ff003315',
              border: '1px solid #ff003340',
              borderRadius: '0.5rem',
              padding: '0.6rem',
              marginBottom: '0.75rem',
              color: '#ff8095',
              fontSize: '0.8rem'
            }}>
              {pwdError}
            </div>
          )}

          <button
            type="submit"
            disabled={pwdLoading}
            style={{
              width: '100%',
              padding: '0.85rem',
              background: pwdLoading ? '#333' : 'linear-gradient(135deg, #00ffff, #0088ff)',
              border: 'none',
              borderRadius: '0.5rem',
              color: pwdLoading ? '#666' : '#000',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: pwdLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {pwdLoading ? 'Guardando...' : 'Actualizar contraseña'}
          </button>
        </form>
      </div>

      {/* Acciones (no pendiente) */}
      {!isPending && (
        <div style={{
          background: '#111111',
          border: '1px solid #222222',
          borderRadius: '1rem',
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'none',
              border: 'none',
              borderBottom: '1px solid #222222',
              color: '#00ffff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}
          >
            <WalletIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            Mi Wallet / Saldo
          </button>

          {user.role === 'admin' || user.role === 'owner' ? (
            <button
              onClick={() => navigate('/admin')}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'none',
                border: 'none',
                color: '#ff00ff',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer'
              }}
            >
              <ShieldCheckIcon style={{ width: '1.25rem', height: '1.25rem' }} />
              Panel de Administración
            </button>
          ) : null}
        </div>
      )}

      {/* Logout */}
      <div style={{
        background: '#111111',
        border: '1px solid #222222',
        borderRadius: '1rem',
        padding: '1rem'
      }}>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '1rem',
            background: 'none',
            border: 'none',
            color: '#ff0033',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer'
          }}
        >
          <ArrowRightOnRectangleIcon style={{ width: '1.25rem', height: '1.25rem' }} />
          Cerrar Sesión
        </button>
      </div>
    </div>
  );
}

export default Profile;